import React, {useEffect, useMemo, useState} from 'react';
import ArrModal from '../../common/arr-modal/ArrModal';
import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import {ReactComponent as InfoIcon} from '../../../assets/images/info-icon.svg';
import {ReactComponent as DownloadIcon} from '../../../assets/images/download_icon.svg';
import ArrTooltipWrapper from '../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {useGetRedactionTags} from '../../../hooks/document/useDocument';
import {
  useBatchRedaction,
  usePreRedactionReportDownload,
} from '../../../hooks/redactions/useBatchRedaction';
import {ToastType} from '../../../shared/constants';
import {ISelectItem} from '../../../shared/types';
import {Trans} from 'react-i18next';
import ArrDropdown from '../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {IApplyRedactionData} from '../../../api/types/redactions.type';
import './ViewReportModal.scss';
import ArrButton from '../../common/arr-button/ArrButton';

interface IViewReportModal {
  idList: string[];
  showReportModal: boolean;
  analysisResultDetails: IApplyRedactionData[];
  onCancel: () => void;
  onConfirm: () => void;
  triggerToast: (status: ToastType, message: string) => void;
  onRedactionTagChange?: (tag: any) => void;
  initialSelectedRedactionTag?: any;
}

const ViewReportModal: React.FC<IViewReportModal> = ({
  idList,
  analysisResultDetails,
  showReportModal,
  onCancel,
  onConfirm,
  triggerToast,
  onRedactionTagChange,
  initialSelectedRedactionTag,
}) => {
  const [selectedRedactionTag, setSelectedRedactionTag] = useState<any>(
    initialSelectedRedactionTag
  );
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const {data: redactionTags} = useGetRedactionTags(true);
  const {mutate: downloadPreRedactionReportDownload} =
    usePreRedactionReportDownload();

  const {mutate: batchRedaction, isPending} = useBatchRedaction();

  useEffect(() => {
    if (
      redactionTags?.redactionTagDetails?.length &&
      !initialSelectedRedactionTag
    ) {
      setSelectedRedactionTag(redactionTags.redactionTagDetails[0]);
    }
  }, [redactionTags]);

  useEffect(() => {
    if (selectedRedactionTag && onRedactionTagChange) {
      onRedactionTagChange(selectedRedactionTag);
    }
  }, [selectedRedactionTag]);

  const isRedactable = useMemo(() => {
    return analysisResultDetails.some(
      (resultItem) => resultItem.expectedTags > 0
    );
  }, [analysisResultDetails]);

  const getBatchRedactionLabel = () => {
    const searchLabelList = analysisResultDetails.map(
      (item) => item.searchLabel
    );
    return searchLabelList.join(', ');
  };

  const performBatchRedaction = () => {
    const payload = {
      selectedSearchGroups: {selected: idList},
      selectedRedactionTagId: selectedRedactionTag.redactionID,
      selectedRedactionTagLabel: selectedRedactionTag.name,
      selectedSearchLabel: getBatchRedactionLabel(),
    };
    batchRedaction(payload, {
      onSuccess: () => {
        onConfirm?.();
        triggerToast?.(
          ToastType.Success,
          t('batchRedactions.modal.redactSuccess')
        );
      },
      onError: (error: Error) => {
        triggerToast?.(ToastType.Error, error.message);
      },
    });
    setShowConfirmationModal(false);
  };

  const onDownloadClick = () => {
    downloadPreRedactionReportDownload(
      {
        actionCode: 'BRPVW',
        batchRedactionId: '0',
        batchRedactionTag: '',
        batchRedactionTitle: getBatchRedactionLabel(),
        exportContextType: 'BATCHREDACTION',
        isBatchRedactionRolledBack: false,
        searchIdList: idList,
      },
      {
        onSuccess: () => {
          triggerToast?.(
            ToastType.Success,
            t('batchRedactions.modal.reportSuccessMessage')
          );
          onCancel?.();
        },
        onError: (error: Error) => {
          triggerToast?.(ToastType.Error, error.message);
        },
      }
    );
  };

  return (
    <>
      <ArrModal
        showModal={showReportModal}
        autoFocusCloseButton={true}
        ariaLabel={t('batchRedactions.modal.title')}
        title={t('batchRedactions.modal.title')}
        dataTestId="view-report-modal"
        description={
          <Box className="view-report-modal">
            <Box className="data">
              {analysisResultDetails.map((i: any, index) => (
                <Box key={i.id} tabIndex={0}>
                  <Typography
                    className="typo-text subhead4_size title"
                    data-testid={`view-report-modal-searchLabel-${index}`}
                  >
                    {i.searchLabel}
                  </Typography>

                  <Box className="row">
                    <Typography
                      className="typo-text body3_size"
                      data-testid={`view-report-modal-data-label1-${index}`}
                    >
                      {t('batchRedactions.modal.dataLabel1')}
                    </Typography>
                    <Typography
                      className="typo-text body3_size"
                      data-testid={`view-report-modal-numDocs--${index}`}
                    >
                      {i.numDocs}
                    </Typography>
                  </Box>
                  <Box className="row">
                    <Typography
                      className="typo-text body3_size"
                      data-testid={`view-report-modal-data-label2`}
                    >
                      {t('batchRedactions.modal.dataLabel2')}
                    </Typography>
                    <Typography
                      className="typo-text body3_size"
                      data-testid={`view-report-modal-num-redactable-${index}`}
                    >
                      {i.numRedactable}
                    </Typography>
                  </Box>
                  <Box className="row">
                    <Typography
                      className="typo-text body3_size"
                      data-testid={`view-report-modal-data-label3`}
                    >
                      {t('batchRedactions.modal.dataLabel3')}
                    </Typography>
                    <Typography
                      className="typo-text body3_size"
                      data-testid={`view-report-modal-expected--${index}`}
                    >
                      {i.expectedTags}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
            <Box className="dropdown-container">
              <Box>
                <Typography
                  className="typo-text body3_size"
                  data-testid={`view-report-modal-select-redaction-tag`}
                >
                  {t('batchRedactions.modal.selectRedactionTag')}
                </Typography>
                <ArrTooltipWrapper
                  trigger={
                    <InfoIcon
                      fontSize="medium"
                      className="info-icon"
                      data-testid="info-icon"
                      tabIndex={0}
                      aria-label={t('common.infoTooltip')}
                    />
                  }
                  closeTooltipOnEscPress
                >
                  <Trans
                    i18nKey={t('batchRedactions.modal.tooltip')}
                    components={{
                      br: <br data-testid={`view-report-modal-br`} />,
                      h3: (
                        <Typography
                          component="h3"
                          data-testid={`view-report-modal-typography-icon`}
                        />
                      ),
                    }}
                  ></Trans>
                </ArrTooltipWrapper>
              </Box>
              {redactionTags?.redactionTagDetails?.length && (
                <ArrDropdown
                  sizeVariant="medium"
                  dropdownLabel={t(`common.select`)}
                  dropdownItemKey="documentID"
                  selectedValues={selectedRedactionTag}
                  dropdownItems={
                    redactionTags.redactionTagDetails as unknown as ISelectItem[]
                  }
                  dataTestId="select-redaction-tag-dropdown"
                  onSelectionChange={(item) => setSelectedRedactionTag(item)}
                  tabIndex={-1}
                />
              )}
            </Box>
            <Box className="download-redaction-button">
              <ArrButton
                variant="text"
                size="small"
                className="download-button"
                dataTestId="download-button"
                startIcon={<DownloadIcon />}
                aria-label={t('assignment.manageReviewers.addreviewers')}
                onClick={onDownloadClick}
                id="download-button"
                name="download-button"
              >
                {t('batchRedactions.modal.downloadPreRedactionReport')}
              </ArrButton>
            </Box>
          </Box>
        }
        onClose={() => onCancel?.()}
        buttons={[
          {
            id: 'cancel-button',
            label: t('common.close'),
            dataTestId: 'vrm-cancel-button',
            onClick: () => onCancel?.(),
            variant: 'secondary',
          },
          {
            id: 'status-button',
            label: t('batchRedactions.modal.redact'),
            dataTestId: 'keyword-delete-confirm-button',
            onClick: () => setShowConfirmationModal(true),
            variant: 'primary',
            disabled: !isRedactable,
          },
        ]}
      />
      <ArrModal
        descriptionHasHtml={true}
        showModal={showConfirmationModal}
        ariaLabel={t('batchRedactions.modal.confirmationModal.title')}
        title={t('batchRedactions.modal.confirmationModal.title')}
        ariaDescription={t(
          'batchRedactions.modal.confirmationModal.description'
        )}
        description={t('batchRedactions.modal.confirmationModal.description')}
        onClose={() => setShowConfirmationModal(false)}
        autoFocusCloseButton={true}
        dataTestId="view-report-confirmation-modal"
        buttons={[
          {
            id: 'cancel-button',
            label: t('common.cancel'),
            dataTestId: 'vrcm-cancel-button',
            onClick: () => setShowConfirmationModal(false),
            variant: 'secondary',
            disabled: isPending,
          },
          {
            id: 'continue-button',
            label: t('common.continue'),
            dataTestId: 'vrcm-continue-button',
            onClick: performBatchRedaction,
            variant: 'primary',
            loading: isPending,
            disabled: isPending,
          },
        ]}
      />
    </>
  );
};

export default ViewReportModal;
